// The old pane's in-memory selection and input, scoped to one injected
// client. Replacing the auth/workspace client never reuses pending bytes.
import { InputQueue, type InputSender } from './input'
import type { RunRequest } from './types'

interface Memory {
  key: number
  inputs: Map<string, InputQueue>
  front: Map<string, string>
}
const clients = new WeakMap<object, Memory>()
const released = new WeakSet<object>()
// A click remains claimed across all views while any copy retains its token.
// Weak identity retains neither completed commands nor an unbounded id ledger.
const claimedRuns = new WeakSet<object>()
let next = 0

function memory(client: object): Memory {
  let held = clients.get(client)
  if (!held) {
    held = { key: ++next, inputs: new Map(), front: new Map() }
    clients.set(client, held)
  }
  return held
}

/** A different client remounts the pane and discards its old callbacks. */
export const clientKey = (client: object): number => memory(client).key

export function inputOf(client: object, id: string, send: InputSender, expects?: unknown): InputQueue {
  const inputs = memory(client).inputs
  let queue = inputs.get(id)
  if (queue) {
    queue.use(send)
    queue.expect(expects)
  } else {
    queue = new InputQueue(send, typeof expects === 'number' && Number.isSafeInteger(expects) && expects >= 1 ? expects : 1)
    if (released.has(client)) queue.close()
    inputs.set(id, queue)
  }
  return queue
}

export function expectInput(client: object, id: string, seq: unknown): void {
  memory(client).inputs.get(id)?.expect(seq)
}

/** Type again into a terminal whose input failed, from the seq a fresh list says (InputQueue.resume). */
export function reviveInput(client: object, id: string, seq: unknown, explicit = false): boolean {
  if (released.has(client)) return false
  return clients.get(client)?.inputs.get(id)?.resume(seq, explicit) ?? false
}

/** The terminal has a queue here that takes input now: revived by this view or another of the same client. */
export function inputLive(client: object, id: string): boolean {
  const queue = clients.get(client)?.inputs.get(id)
  return queue !== undefined && queue.stopped === null
}

export function forgetTerminal(client: object, id: string): void {
  const held = memory(client)
  held.inputs.get(id)?.close()
  held.inputs.delete(id)
  for (const [session, terminal] of held.front) if (terminal === id) held.front.delete(session)
}

export const frontTerminal = (client: object, session: string): string | null => memory(client).front.get(session) ?? null
export const bringToFront = (client: object, session: string, terminal: string): void => {
  if (!released.has(client)) memory(client).front.set(session, terminal)
}

export function claimRun(client: object, request: RunRequest): boolean {
  if (released.has(client) || request.client !== client) return false
  if (typeof request.intent !== 'object' || request.intent === null || claimedRuns.has(request.intent)) return false
  claimedRuns.add(request.intent)
  return true
}

/** The transport calls this on sign-out/scope disposal, not when a pane hides. */
export function releaseTerminalClient(client: object): void {
  released.add(client)
  const held = clients.get(client)
  if (!held) return
  for (const queue of held.inputs.values()) queue.close()
  held.inputs.clear()
  held.front.clear()
}

/** Tests alone forget a client's retained queues. */
export function resetPaneMemoryForTests(client: object): void {
  for (const queue of memory(client).inputs.values()) queue.close()
  clients.delete(client)
}
